import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="quiz-keyboard"
// Handles keyboard shortcuts (A-E keys) for toggling radio buttons and timing updates
export default class extends Controller {
  static targets = ["stopwatch", "answerTime", "timeDisplay"]
  static values = {
    stopwatchId: { type: String, default: "stopwatch" },
    answerTimeId: { type: String, default: "anstime" },
    timeDisplayId: { type: String, default: "t7" }
  }

  connect() {
    // Set up keyboard event listener
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    // Clean up event listener
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  handleKeydown(event) {
    // Handle A-E keys (keyCode 65-69)
    if (event.keyCode >= 65 && event.keyCode <= 69) {
      const radioNumber = event.keyCode - 64 // A=1, B=2, C=3, D=4, E=5
      const radioId = `r${radioNumber}`
      const radioInput = document.getElementById(radioId)

      if (!radioInput) {
        console.warn(`[quiz-keyboard] Radio button #${radioId} not found`)
        return
      }

      // Toggle the radio button
      if (radioInput.checked) {
        radioInput.checked = false
      } else {
        radioInput.checked = true
      }

      console.log(`[quiz-keyboard] Key pressed: ${event.keyCode}, toggled radio ${radioId}`)

      // Update timing after keypress
      this.updateTiming()
    }
  }

  updateTiming() {
    // Get stopwatch element
    const stopwatch = this.getStopwatchElement()
    if (!stopwatch) {
      console.warn('[quiz-keyboard] Stopwatch element not found')
      return
    }

    // Get current time from stopwatch (format: MM:SS)
    // Try multiple methods to get the time
    let curTime = stopwatch.textContent || stopwatch.innerHTML || ''

    // Also try getting from data-clock attribute (used by timer controller)
    if (!curTime || curTime.trim() === '') {
      const dataClock = stopwatch.getAttribute('data-clock')
      if (dataClock) {
        // data-clock is in seconds, convert to MM:SS format for parsing
        const totalSeconds = parseFloat(dataClock)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = Math.floor(totalSeconds % 60)
        curTime = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      }
    }

    // If still no time, default to 00:00
    if (!curTime || curTime.trim() === '' || curTime === '00:00') {
      curTime = '00:00'
    }

    // Validate time format (should be MM:SS)
    if (curTime.length < 5 || !curTime.includes(':')) {
      console.warn('[quiz-keyboard] Invalid time format:', curTime)
      return
    }

    // Parse time: MM:SS -> total seconds
    const timeParts = curTime.split(':')
    if (timeParts.length !== 2) {
      console.warn('[quiz-keyboard] Invalid time format:', curTime)
      return
    }

    const curTimeS1 = timeParts[0] || '00' // minutes
    const curTimeS2 = timeParts[1] || '00' // seconds
    const curTimeTotal = (parseInt(curTimeS1, 10) * 60) + parseInt(curTimeS2, 10)

    // Update answer time hidden input
    const answerTimeInput = this.getAnswerTimeElement()
    if (answerTimeInput) {
      answerTimeInput.value = curTimeTotal
    }

    // Update time display element (if it exists)
    const timeDisplay = this.getTimeDisplayElement()
    if (timeDisplay) {
      if (window.jQuery) {
        window.jQuery(timeDisplay).html(curTimeTotal)
      } else {
        timeDisplay.textContent = curTimeTotal
      }
    }
  }

  getStopwatchElement() {
    if (this.hasStopwatchTarget) {
      return this.stopwatchTarget
    }
    return document.getElementById(this.stopwatchIdValue)
  }

  getAnswerTimeElement() {
    if (this.hasAnswerTimeTarget) {
      return this.answerTimeTarget
    }
    return document.getElementById(this.answerTimeIdValue)
  }

  getTimeDisplayElement() {
    if (this.hasTimeDisplayTarget) {
      return this.timeDisplayTarget
    }
    const element = document.getElementById(this.timeDisplayIdValue)
    return element || null // Return null if not found (it's optional)
  }
}

